import { useState } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/app/common";
import { DataTable } from "@/components/app/DataTable";
import { DocumentsPanel } from "@/components/app/DocumentsPanel";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { fmtDate, inr } from "@/lib/format";

const FLAGS = [["lr", "LR pending", "LR Copy", "LR"], ["pod", "POD pending", "POD", "POD"], ["freight_invoice", "Freight Invoice Pending", "Transporter Invoice", "freight invoice"]];

function UploadDialog({ row, flag, onClose, onDone }) {
  const [, title, docType, short] = FLAGS.find(([k]) => k === flag);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="tdoc-upload-dialog">
        <DialogHeader><DialogTitle>Upload {short} · <span className="font-mono">{row.ref}</span></DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">{title.replace(" pending", "").replace(" Pending", "")} for {row.order_nos.filter(Boolean).join(", ") || row.party}. {flag === "pod" ? "Uploading the POD marks POD received." : flag === "freight_invoice" ? "Uploading the freight invoice marks it received on the movement." : ""}</p>
        <DocumentsPanel entityType={row.kind} entityId={row.id} onlyType={docType} hideList uploadLabel={`Upload ${short}`} onChange={onDone} />
      </DialogContent>
    </Dialog>
  );
}

// Pending LR / POD / freight invoice lists with one-click upload
export function TransportDocs({ onOpenMovement, tick, children }) {
  const { can } = useMe();
  const [flag, setFlag] = useState("lr");
  const [k, setK] = useState(0);
  const [up, setUp] = useState(null);
  const { data } = useFetch(`/logistics/transport-docs?flag=${flag}&t=${tick}-${k}`);
  const fi = flag === "freight_invoice";
  const short = FLAGS.find(([x]) => x === flag)[3];
  const cols = [
    { key: "ref", label: fi ? "Movement" : "Dispatch", render: (r) => <span className="font-mono font-semibold text-sky-800">{r.ref}</span> },
    ...(fi ? [] : [{ key: "movement_no", label: "Movement", mono: true }]),
    { key: "order_nos", label: "LMs", wrap: true, value: (r) => r.order_nos.join(", "), render: (r) => r.order_nos.join(", ") || "—" },
    { key: "party", label: "Customer / party", wrap: true }, { key: "transporter", label: "Transporter" },
    { key: "date", label: "Date", render: (r) => fmtDate(r.date) }, { key: "lr_no", label: "LR no.", mono: true },
    fi ? { key: "actual_cost", label: "Actual cost", right: true, render: (r) => inr(r.actual_cost) } : { key: "pod_status", label: "POD", render: (r) => <StatusBadge status={`pod_${r.pod_status || "pending"}`} /> },
    { key: "up", label: "", exp: false, render: (r) => can("write") && <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setUp(r); }} data-testid={`tdoc-upload-${r.ref}`}><Upload className="h-4 w-4 mr-1" />Upload {short}</Button> },
  ];
  return (
    <div data-testid="transport-docs">
      <div className="mb-3 flex flex-wrap gap-2" data-testid="tdoc-filters">
        {FLAGS.map(([key, l]) => <button key={key} onClick={() => setFlag(key)} data-testid={`tdoc-filter-${key}`}
          className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${flag === key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>{l}</button>)}
      </div>
      <DataTable testid="tdoc-table" columns={cols} rows={data} onRowClick={(r) => r.movement_id && onOpenMovement(r.movement_id)} exportName={`Transport_docs_${flag}`} exportPerm="logistics.export" empty="Nothing pending here" />
      {up && <UploadDialog row={up} flag={flag} onClose={() => setUp(null)} onDone={() => { setUp(null); setK((x) => x + 1); }} />}
      {fi && <div className="mt-6">{children}</div>}
    </div>
  );
}
